import { Input, Node } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { BaseComponent } from "./base_component"
import { categoricalBooleanDataSocket, categoricalDataSocket, numericDataSocket, tableDataSocket } from "../socket_types"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../tile_grid"
import { TableDataset } from "../table_dataset"
import { maskFromExtentAndShape } from "../bounding_box"
import type { ProjectProperties } from "./index"
import { DatasetChartControl, SpiderChartControl, TableChartControl, TableCompareChartControl } from "../controls/dataset_chart"
import { ExposeToggleControl } from "../controls/expose_toggle_control"
import {
  DATASET_CHART_KEY,
  DEFAULT_HISTOGRAM_BINS,
  HISTOGRAM_FILL,
  TABLE_CHART_KEY,
  SPIDER_CHART_KEY,
  TABLE_COMPARE_CHART_KEY,
  datasetToChartData,
  readHistogramBins,
  spiderChartFromInputs,
  spiderChartHasData,
  tableChartFromInputs,
  tableCompareChartFromInputs,
  tableCompareDifference,
  type DatasetChartKind,
} from "../dataset_chart_data"

export const PIE_CHART_COMPONENT_NAME = "Pie chart"
export const BAR_DISTRIBUTION_CHART_COMPONENT_NAME = "Bar distribution chart"
export const HISTOGRAM_CHART_COMPONENT_NAME = "Histogram"
export const TABLE_CHART_COMPONENT_NAME = "Table chart"
export const TABLE_COMPARE_CHART_COMPONENT_NAME = "Table compare chart"
export const SPIDER_CHART_COMPONENT_NAME = "Spider diagram"

const CHART_CONTROL_KEY = "dataset-chart"
const TABLE_CHART_CONTROL_KEY = "table-chart"
const TABLE_COMPARE_CHART_CONTROL_KEY = "table-compare-chart"
const SPIDER_CHART_CONTROL_KEY = "spider-chart"

async function loadChartMask(projectProps: ProjectProperties): Promise<BooleanTileGrid | undefined> {
  if (!projectProps.mask) return undefined
  return maskFromExtentAndShape(projectProps)
}

export class DatasetChartComponent extends BaseComponent {
  readonly chartKind: DatasetChartKind
  private readonly projectProps: ProjectProperties

  constructor(name: string, chartKind: DatasetChartKind, projectProps: ProjectProperties) {
    super(name)
    this.category = "Charts"
    this.chartKind = chartKind
    this.projectProps = projectProps
  }

  async builder(node: Node) {
    if (!("expose" in node.data)) {
      node.data.expose = node.data.showInDetail === true
    }
    if (this.chartKind === "hist" && node.data.histogramBins == null) {
      node.data.histogramBins = DEFAULT_HISTOGRAM_BINS
    }

    const socket = this.chartKind === "hist" ? numericDataSocket : categoricalBooleanDataSocket
    const inputLabel = this.chartKind === "hist" ? "Numeric dataset" : "Categorical or Boolean"
    node.addInput(new Input("in", inputLabel, socket))

    if (!node.controls.has("expose")) {
      node.addControl(new ExposeToggleControl(this.editor, "expose"))
    }
    node.addControl(new DatasetChartControl(this.editor, CHART_CONTROL_KEY, this.chartKind))
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(item => item.id === node.id)
    if (editorNode === undefined) return

    const control = editorNode.controls.get(CHART_CONTROL_KEY) as (DatasetChartControl & { update: () => void }) | undefined
    const input = inputs["in"]?.[0]
    const bins = readHistogramBins(editorNode.data.histogramBins)

    const clear = (message: string) => {
      editorNode.meta.errorMessage = message
      delete editorNode.data[DATASET_CHART_KEY]
      control?.setChart(null)
      control?.update()
      editorNode.update()
    }

    if (this.chartKind === "hist") {
      if (!(input instanceof NumericTileGrid)) {
        clear("Connect a numeric dataset")
        return
      }
      delete editorNode.meta.errorMessage
      const chart = datasetToChartData(input, { bins, fillType: HISTOGRAM_FILL, mask: await loadChartMask(this.projectProps) })
      editorNode.data[DATASET_CHART_KEY] = chart
      control?.setChart(chart)
      control?.update()
      editorNode.update()
      return
    }

    if (!(input instanceof BooleanTileGrid || input instanceof CategoricalTileGrid)) {
      clear("Connect a categorical or boolean dataset")
      return
    }

    delete editorNode.meta.errorMessage
    const chart = datasetToChartData(input, { bins, fillType: HISTOGRAM_FILL, mask: await loadChartMask(this.projectProps) })
    editorNode.data[DATASET_CHART_KEY] = chart
    control?.setChart(chart)
    control?.update()
    editorNode.update()
  }
}

export class TableChartComponent extends BaseComponent {
  private readonly projectProps: ProjectProperties

  constructor(projectProps: ProjectProperties) {
    super(TABLE_CHART_COMPONENT_NAME)
    this.category = "Charts"
    this.projectProps = projectProps
  }

  async builder(node: Node) {
    if (!("expose" in node.data)) {
      node.data.expose = node.data.showInDetail === true
    }

    node.addInput(new Input("categories", "Categorical dataset", categoricalDataSocket))
    node.addInput(new Input("table", "Table", tableDataSocket))

    if (!node.controls.has("expose")) {
      node.addControl(new ExposeToggleControl(this.editor, "expose"))
    }
    node.addControl(new TableChartControl(TABLE_CHART_CONTROL_KEY))
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(item => item.id === node.id)
    if (editorNode === undefined) return

    const control = editorNode.controls.get(TABLE_CHART_CONTROL_KEY) as (TableChartControl & { update: () => void }) | undefined
    const categories = inputs["categories"]?.[0]
    const table = inputs["table"]?.[0]

    const clear = (message: string) => {
      editorNode.meta.errorMessage = message
      delete editorNode.data[TABLE_CHART_KEY]
      control?.setChart(null)
      control?.update()
      editorNode.update()
    }

    if (!(categories instanceof CategoricalTileGrid)) {
      clear("Connect a categorical dataset")
      return
    }
    if (!(table instanceof TableDataset)) {
      clear("Connect a table")
      return
    }

    const chart = tableChartFromInputs(categories, table, await loadChartMask(this.projectProps))
    if (chart.rows.length === 0) {
      editorNode.meta.errorMessage = "Table columns do not match the category labels, or the matching cells are empty"
      editorNode.data[TABLE_CHART_KEY] = chart
      control?.setChart(chart)
      control?.update()
      editorNode.update()
      return
    }

    delete editorNode.meta.errorMessage
    editorNode.data[TABLE_CHART_KEY] = chart
    control?.setChart(chart)
    control?.update()
    editorNode.update()
  }
}

export class TableCompareChartComponent extends BaseComponent {
  private readonly projectProps: ProjectProperties

  constructor(projectProps: ProjectProperties) {
    super(TABLE_COMPARE_CHART_COMPONENT_NAME)
    this.category = "Charts"
    this.projectProps = projectProps
  }

  async builder(node: Node) {
    if (!("expose" in node.data)) {
      node.data.expose = node.data.showInDetail === true
    }

    node.addInput(new Input("a", "A", categoricalDataSocket))
    node.addInput(new Input("b", "B", categoricalDataSocket))
    node.addInput(new Input("table", "Table", tableDataSocket))

    if (!node.controls.has("expose")) {
      node.addControl(new ExposeToggleControl(this.editor, "expose"))
    }
    node.addControl(new TableCompareChartControl(TABLE_COMPARE_CHART_CONTROL_KEY))
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(item => item.id === node.id)
    if (editorNode === undefined) return

    const control = editorNode.controls.get(TABLE_COMPARE_CHART_CONTROL_KEY) as (TableCompareChartControl & { update: () => void }) | undefined
    const categoriesA = inputs["a"]?.[0]
    const categoriesB = inputs["b"]?.[0]
    const table = inputs["table"]?.[0]

    const clear = (message: string) => {
      editorNode.meta.errorMessage = message
      delete editorNode.data[TABLE_COMPARE_CHART_KEY]
      control?.setChart(null)
      control?.update()
      editorNode.update()
    }

    if (!(categoriesA instanceof CategoricalTileGrid)) {
      clear("Connect categorical dataset A")
      return
    }
    if (!(categoriesB instanceof CategoricalTileGrid)) {
      clear("Connect categorical dataset B")
      return
    }
    if (!(table instanceof TableDataset)) {
      clear("Connect a table")
      return
    }

    const chart = tableCompareChartFromInputs(categoriesA, categoriesB, table, await loadChartMask(this.projectProps))
    const difference = tableCompareDifference(chart, false)
    if (difference.length === 0) {
      editorNode.meta.errorMessage = chart.rowsA.length === 0 || chart.rowsB.length === 0
        ? "Table columns do not match the category labels, or the matching cells are empty"
        : "A and B have no table rows in common"
      editorNode.data[TABLE_COMPARE_CHART_KEY] = chart
      control?.setChart(chart)
      control?.update()
      editorNode.update()
      return
    }

    delete editorNode.meta.errorMessage
    editorNode.data[TABLE_COMPARE_CHART_KEY] = chart
    control?.setChart(chart)
    control?.update()
    editorNode.update()
  }
}

export class SpiderChartComponent extends BaseComponent {
  private readonly projectProps: ProjectProperties

  constructor(projectProps: ProjectProperties) {
    super(SPIDER_CHART_COMPONENT_NAME)
    this.category = "Charts"
    this.projectProps = projectProps
  }

  async builder(node: Node) {
    if (!("expose" in node.data)) {
      node.data.expose = node.data.showInDetail === true
    }

    node.addInput(new Input("categories", "Categorical datasets", categoricalDataSocket, true))
    node.addInput(new Input("table", "Table", tableDataSocket))

    if (!node.controls.has("expose")) {
      node.addControl(new ExposeToggleControl(this.editor, "expose"))
    }
    node.addControl(new SpiderChartControl(SPIDER_CHART_CONTROL_KEY))
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
    const editorNode = this.editor?.nodes.find(item => item.id === node.id)
    if (editorNode === undefined) return

    const control = editorNode.controls.get(SPIDER_CHART_CONTROL_KEY) as (SpiderChartControl & { update: () => void }) | undefined
    const connected = inputs["categories"] ?? []
    const grids = connected.filter((item): item is CategoricalTileGrid => item instanceof CategoricalTileGrid)
    const table = inputs["table"]?.[0]

    const clear = (message: string) => {
      editorNode.meta.errorMessage = message
      delete editorNode.data[SPIDER_CHART_KEY]
      control?.setChart(null)
      control?.update()
      editorNode.update()
    }

    if (grids.length === 0 || grids.length !== connected.length) {
      clear("Connect at least one categorical dataset")
      return
    }
    if (!(table instanceof TableDataset)) {
      clear("Connect a table")
      return
    }

    const chart = spiderChartFromInputs(grids, table, await loadChartMask(this.projectProps))
    if (!spiderChartHasData(chart)) {
      editorNode.meta.errorMessage = "Table columns do not match the category labels, or the matching cells are empty"
      editorNode.data[SPIDER_CHART_KEY] = chart
      control?.setChart(chart)
      control?.update()
      editorNode.update()
      return
    }

    delete editorNode.meta.errorMessage
    editorNode.data[SPIDER_CHART_KEY] = chart
    control?.setChart(chart)
    control?.update()
    editorNode.update()
  }
}
